import { PartItem } from '@cutr/constants/cutlist';
import { Features } from '@cutr/constants/cutlist-theme';
import {
  classifySmallPartEdgebanding,
  EdgebandingSide,
  SMALL_PART_EDGEBANDING_LIMITS,
  SmallPartEdgebandingLimits,
} from '@cutr/constants/small-part-edgebanding';

export type SmallPartEdgebandingNotice = {
  severity: 'error' | 'warning';
  key: string;
  reasonKey?: string;
  manualEdges: EdgebandingSide[];
  grossLengthMM: number;
  grossWidthMM: number;
};

const MANUAL_KEYS: Record<Features['manualEdgebandingRule'], string> = {
  'not-allowed': 'cutlist-form.errors.smallPartEdgebanding.manual.notAllowed',
  included: 'cutlist-form.errors.smallPartEdgebanding.manual.included',
  'by-customer': 'cutlist-form.errors.smallPartEdgebanding.manual.byCustomer',
};

export const SIDE_LABEL_KEYS: Record<EdgebandingSide, string> = {
  length1: 'cutlist-form.field.edgebanding.sides.l1',
  length2: 'cutlist-form.field.edgebanding.sides.l2',
  width1: 'cutlist-form.field.edgebanding.sides.w1',
  width2: 'cutlist-form.field.edgebanding.sides.w2',
};

type ClassifiablePart = Pick<
  PartItem,
  'partType' | 'lengthMM' | 'widthMM' | 'edgebanding'
>;

function classify(
  part: ClassifiablePart,
  limits: SmallPartEdgebandingLimits = SMALL_PART_EDGEBANDING_LIMITS
) {
  const { edgebanding } = part;
  const bandedEdges = edgebanding
    ? (Object.keys(edgebanding) as EdgebandingSide[]).filter(
        (side) => edgebanding[side]
      )
    : [];

  return classifySmallPartEdgebanding(
    {
      partType: part.partType,
      lengthMM: +part.lengthMM,
      widthMM: +part.widthMM,
      bandedEdges,
    },
    limits
  );
}

export function getNestingDimensions(
  part: ClassifiablePart,
  limits: SmallPartEdgebandingLimits | undefined
) {
  if (!limits) return { lengthMM: +part.lengthMM, widthMM: +part.widthMM };

  const { grossLengthMM, grossWidthMM } = classify(part, limits);
  return { lengthMM: grossLengthMM, widthMM: grossWidthMM };
}

export function getSmallPartEdgebandingDimensions(
  part: ClassifiablePart
): ('lengthMM' | 'widthMM')[] {
  const { grossLengthMM, grossWidthMM, manualEdges } = classify(part);
  const lengthAffected =
    grossLengthMM > +part.lengthMM ||
    manualEdges.some((side) => side.startsWith('width'));
  const widthAffected =
    grossWidthMM > +part.widthMM ||
    manualEdges.some((side) => side.startsWith('length'));

  return [
    ...(lengthAffected ? ['lengthMM' as const] : []),
    ...(widthAffected ? ['widthMM' as const] : []),
  ];
}

export function getManualEdges(part: ClassifiablePart) {
  return classify(part).manualEdges;
}

export function needsPresaw(part: ClassifiablePart) {
  return classify(part).outcome !== 'no-presaw';
}

export function hasManualEdgebanding(part: ClassifiablePart) {
  return getManualEdges(part).length > 0;
}

export function getManualEdgebandingSummary(
  parts: ClassifiablePart[],
  rule: Features['manualEdgebandingRule']
) {
  const count = parts.filter(hasManualEdgebanding).length;
  return { rule, count, isBlocked: rule === 'not-allowed' && count > 0 };
}

export function getSmallPartEdgebandingNotice(
  part: ClassifiablePart,
  rule: Features['manualEdgebandingRule']
): SmallPartEdgebandingNotice | null {
  const { outcome, manualEdges, grossLengthMM, grossWidthMM } = classify(part);

  if (outcome === 'no-presaw') return null;
  if (outcome === 'presaw') {
    return {
      severity: 'warning',
      key: 'cutlist-form.errors.smallPartEdgebanding.presaw',
      manualEdges,
      grossLengthMM,
      grossWidthMM,
    };
  }
  return {
    severity: rule === 'not-allowed' ? 'error' : 'warning',
    key: MANUAL_KEYS[rule],
    reasonKey:
      manualEdges.length > 1
        ? 'cutlist-form.errors.smallPartEdgebanding.manual.reason.twoEdges'
        : 'cutlist-form.errors.smallPartEdgebanding.manual.reason.oneEdge',
    manualEdges,
    grossLengthMM,
    grossWidthMM,
  };
}
